import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { defaultTheme } from "./defaultTheme";
import { applyTheme, normalizeTheme } from "./themeUtils";
import { defaultSite, mergeSite } from "../content/site";
import { loadSettings, readCachedSettings } from "../lib/settings";

const ThemeContext = createContext({
  theme: defaultTheme,
  site: defaultSite,
  previewTheme: () => {},
  setSiteOverrides: () => {},
  loaded: false,
});

export function ThemeProvider({ children }) {
  const cached = useMemo(() => readCachedSettings(), []);
  const [theme, setTheme] = useState(() => normalizeTheme(cached?.theme));
  const [siteOverrides, setSiteOverrides] = useState(() => cached?.site || null);
  const [loaded, setLoaded] = useState(false);

  // İlk render'da (önbellekteki) tema hemen uygulanır, sonra Firestore'dan güncellenir.
  useEffect(() => {
    applyTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    // Önizleme iframe'inde Firestore okunmaz; tema ebeveyn pencereden gelir.
    if (window.parent !== window) return undefined;
    loadSettings().then((data) => {
      if (!alive) return;
      if (data?.theme) setTheme(normalizeTheme(data.theme));
      if (data?.site) setSiteOverrides(data.site);
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Admin panelindeki canlı önizleme iframe'i için: aynı origin'den gelen tema/site mesajlarını uygula.
  useEffect(() => {
    if (window.parent === window) return undefined;
    const onMessage = (e) => {
      if (e.origin !== window.location.origin || !e.data) return;
      if (e.data.type === "mu:theme") setTheme(normalizeTheme(e.data.theme));
      if (e.data.type === "mu:site") setSiteOverrides(e.data.site);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      site: mergeSite(siteOverrides),
      previewTheme: (t) => setTheme(normalizeTheme(t)),
      setSiteOverrides,
      loaded,
    }),
    [theme, siteOverrides, loaded]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
export const useSite = () => useContext(ThemeContext).site;
